import { useEffect, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { IChartData, IMultiSeriesChartData } from "../../components";
import { appConfig } from "../../configs";
import { filterConstants } from "../../constants";
import {
  useBalances,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useQueryString,
  useShowHomepageOnboardingModal,
} from "../../hooks";
import {
  ICustomersSummaryDto,
  IKeyValuePairLazy,
  IPayoutsSummaryDto,
  IQueryStringNavLinks,
  ITransactionsSummaryDto,
  IViewTransactionByGatewayChartDto,
  IViewTransactionChartDto,
} from "../../interfaces-and-types";
import {
  totalSalesChartFormatter,
  chartXYFormatter,
  todayGrossVolumeChartXYFormatter,
} from "./chart-data--formatter";
import { View } from "./View";
import { chartTypes, transactionStatus } from "../../enums";
import { AxisOptionsBase } from "react-charts";

interface IQuickSummaryColumDataProps {
  key: string;
  value: number;
  prefix: string;
  suffix: string;
  iconText?: string;
}

export interface IHomeViewProps {
  showOnboardingRequiredModal: boolean;
  closeOnboardingRequiredModal: () => void;
  navigateToAccountSetup: () => void;

  chartPeriodDisplayFormatter: (dateTime: string | Date) => string;

  monthlyOrYearlyQueryStringTabs: IQueryStringNavLinks;
  navigateToRoute: (route: string) => void;
  summaryTableData: (IKeyValuePairLazy & { link_url: string })[];
  quickSummaryColumData: IQuickSummaryColumDataProps[];

  // for charts
  defaultCurrency: string;
  // primary_withdrawal_currency: string;
  timeStampNow: string;
  startChartPeriod: string;
  endChartPeriod: string;

  // payouts
  isLoadingPayoutsSummary: boolean;
  isErrorPayoutsSummary: boolean;
  payoutsSummary: IPayoutsSummaryDto | undefined;

  // todays transactions
  isLoadingTodaysTransaction: boolean;
  isErrorTodaysTransaction: boolean;
  todaysTransactions: IViewTransactionChartDto[] | undefined;

  // transactions by period
  isLoadingTransactionsByPeriod: boolean;
  isErrorTransactionsByPeriod: boolean;
  transactionsByPeriod: IViewTransactionChartDto[];

  // transactions by gateway
  isLoadingTransactionsByGateway: boolean;
  isErrorTransactionsByGateway: boolean;
  transactionsByGateway: IViewTransactionByGatewayChartDto[];

  gatewayChartTxStatus: transactionStatus;
  setGatewayChartTxStatus: (status: transactionStatus) => void;

  chartType: AxisOptionsBase["elementType"];
  setChartType: (chartType: AxisOptionsBase["elementType"]) => void;

  // customers by period
  // isLoadingCustomersByPeriod: xxxxxxx;
  // isErrorCustomersByPeriod: xxxxxxx;
  // customersByPeriod: xxxxxxx;

  // data by category
  todayGrossSales: IChartData[];
  totalSalesChartData: IChartData[];
  totalTransactionsChartData: IChartData[];
  transactionsByGatewayChartData: IMultiSeriesChartData[];
  totalSuccessfulTransactionsChartData: IChartData[];
  totalFailedTransactionsChartData: IChartData[];
  totalCustomersChartData: IChartData[];

  setStartChartPeriodFromDatePicker: (date: string | number | Date) => void;
  setEndChartPeriodFromDatePicker: (date: string | number | Date) => void;
}

export const sumChartValues = (chartValues: IViewTransactionChartDto[]) =>
  chartValues?.reduce((acc, curr) => acc + curr.amount, 0);

const tabValues = {
  monthly: "mnth",
  yearly: "yr",
};

export const Home = () => {
  const { showHomepageOnboardingModal, closeHomepageOnboardingModal } =
    useShowHomepageOnboardingModal(1500);

  const {
    activeBalanceCurrency,
    // setActiveBalanceCurrency,
    // isLoadingBalances,
    // balances,
    // refreshBalances,
  } = useBalances();

  const location = useLocation();
  const query = useQueryString();
  const monthOrYearTabSelected = query.get(
    filterConstants.monthlyOrYearlyStatusKey
  );
  const startDate = query.get(filterConstants.startDateKey);
  const endDate = query.get(filterConstants.endDateKey);

  const [startChartPeriod, setStartChartPeriod] = useState(
    moment().subtract(1, "week").format(appConfig.dateFormat1)
  );
  const [endChartPeriod, setEndChartPeriod] = useState(
    moment().format(appConfig.dateFormat1)
  );

  const [gatewayChartTxStatus, setGatewayChartTxStatus] = useState(
    transactionStatus.successful
  );

  const [chartType, setChartType] = useState<AxisOptionsBase["elementType"]>(
    chartTypes.line
  );

  // set the dashboard url at the address bar on first load
  useEffect(() => {
    if (location.pathname !== "/") navigate("/");
  }, []);

  // useEffect to set the correct start and end chart periods when the tab is changed
  useEffect(() => {
    // console.log(startDate, endDate);
    if (startDate !== null && startDate !== "") {
      setStartChartPeriod(startDate);
      setEndChartPeriod(moment().format(appConfig.dateFormat1));
    } else {
      if (monthOrYearTabSelected === tabValues.monthly) {
        setStartChartPeriod(
          moment().subtract(1, "month").format(appConfig.dateFormat1)
        );
        setEndChartPeriod(moment().format(appConfig.dateFormat1));
      } else if (monthOrYearTabSelected === tabValues.yearly) {
        setStartChartPeriod(
          moment().subtract(1, "year").format(appConfig.dateFormat1)
        );
        setEndChartPeriod(moment().format(appConfig.dateFormat1));
      }
    }
  }, [startDate, endDate]);

  const setStartChartPeriodFromDatePicker = (
    startDateToSet: string | number | Date
  ) =>
    setStartChartPeriod(moment(startDateToSet).format(appConfig.dateFormat1));

  const setEndChartPeriodFromDatePicker = (
    endDateToSet: string | number | Date
  ) => setEndChartPeriod(moment(endDateToSet).format(appConfig.dateFormat1));

  const {
    isLoading: isLoadingCustomersSummary,
    isSuccess: isSuccessCustomersSummary,
    isError: isErrorCustomersSummary,
    data: customersSummary,
  } = useMerchantApiQuery<ICustomersSummaryDto>("getCustomersSummary");

  // const {
  //   isLoading: isLoadingCustomersByPeriod,
  //   isSuccess: isSuccessCustomersByPeriod,
  //   isError: isErrorCustomersByPeriod,
  //   data: customersByPeriod,
  // } = useMerchantApiQueryWithParams<
  //   { startDate: string; endDate: string },
  //   IViewCustomerDto[]
  // >([
  //   "getCustomersByPeriod",
  //   { startDate: startChartPeriod, endDate: endChartPeriod },
  // ]);

  const {
    isLoading: isLoadingTransactionsSummary,
    isSuccess: isSuccessTransactionsSummary,
    isError: isErrorTransactionsSummary,
    data: transactionsSummary,
  } = useMerchantApiQueryWithParams<
    { currency: string },
    ITransactionsSummaryDto
  >(
    [
      "getTransactionsSummary",
      {
        currency: activeBalanceCurrency!,
      },
    ],
    {
      enabled: !!activeBalanceCurrency,
    }
  );

  const {
    isLoading: isLoadingPayoutsSummary,
    isSuccess: isSuccessPayoutsSummary,
    isError: isErrorPayoutsSummary,
    data: payoutsSummary,
  } = useMerchantApiQueryWithParams<{ currency: string }, IPayoutsSummaryDto>(
    [
      "getPayoutsSummary",
      {
        currency: activeBalanceCurrency!,
      },
    ],
    {
      enabled: !!activeBalanceCurrency,
    }
  );

  const {
    isLoading: isLoadingTodaysTransaction,
    // isSuccess: isSuccessTodaysTransaction,
    isError: isErrorTodaysTransaction,
    data: todaysTransactions,
  } = useMerchantApiQueryWithParams<
    { currency: string },
    IViewTransactionChartDto[]
  >(
    [
      "getTodaysTransactions",
      {
        currency: activeBalanceCurrency!,
      },
    ],
    {
      enabled: !!activeBalanceCurrency,
    }
  );

  const {
    isLoading: isLoadingTransactionsByPeriod,
    // isSuccess: isSuccessTransactionsByPeriod,
    isError: isErrorTransactionsByPeriod,
    data: transactionsByPeriod,
  } = useMerchantApiQueryWithParams<
    { startDate: string; endDate: string; currency: string },
    IViewTransactionChartDto[]
  >(
    [
      "getTransactionsByPeriod",
      {
        startDate: startChartPeriod,
        endDate: endChartPeriod,
        currency: activeBalanceCurrency!,
      },
    ],
    {
      enabled: !!activeBalanceCurrency,
    }
  );

  const {
    isLoading: isLoadingTransactionsByGateway,
    // isSuccess: isSuccessTransactionsByGateway,
    isError: isErrorTransactionsByGateway,
    data: transactionsByGateway,
  } = useMerchantApiQueryWithParams<
    { startDate: string; endDate: string; currency: string; status: string },
    IViewTransactionByGatewayChartDto[]
  >(
    [
      "getTransactionsByGatewayByPeriod",
      {
        startDate: startChartPeriod,
        endDate: endChartPeriod,
        currency: activeBalanceCurrency!,
        status: gatewayChartTxStatus,
      },
    ],
    {
      enabled: !!activeBalanceCurrency,
    }
  );

  const quickSummaryColumData: IQuickSummaryColumDataProps[] = [
    {
      key: "Money In",
      value: transactionsSummary?.today_money_in || 0, //+ (topUpsSummary?.today_money_in || 0),
      prefix: activeBalanceCurrency || "",
      suffix: "",
      iconText: `${
        isSuccessPayoutsSummary
          ? payoutsSummary?.percentage_change_money_in
          : "..."
      }%`,
    },
    {
      key: "Money Out",
      value: payoutsSummary?.today_money_out || 0,
      prefix: activeBalanceCurrency || "",
      suffix: "",
      iconText: `${
        isSuccessPayoutsSummary
          ? payoutsSummary?.percentage_change_money_out
          : "..."
      }%`,
    },
    {
      key: "Total Transactions",
      value: transactionsSummary?.today_tx_count || 0,
      prefix: "",
      suffix: "",
    },
    {
      key: "Total Successful",
      value: transactionsSummary?.today_successful_tx_count || 0,
      prefix: "",
      suffix: "",
    },
    {
      key: "Disputes",
      value: transactionsSummary?.today_disputes_count || 0,
      prefix: "",
      suffix: "",
    },
  ];

  const navigate = useNavigate();

  const navigateToRoute = (route: string) => {
    navigate(route);
  };

  const navigateToAccountSetup = () => {
    navigateToRoute("/onboarding");
  };

  const last30DaysFilterQuery = `?startDate=last30days&endDate=today`;

  const summaryTableData: (IKeyValuePairLazy & { link_url: string })[] = [
    {
      key: "Total Customers",
      value: customersSummary?.last30_days_total_customers_count || 0,
      isLoading: isLoadingCustomersSummary,
      isSuccess: isSuccessCustomersSummary,
      isError: isErrorCustomersSummary,
      link_url: `/customers`,
    },
    {
      key: "New Customers",
      value: customersSummary?.last30_days_new_customers_count || 0,
      isLoading: isLoadingCustomersSummary,
      isSuccess: isSuccessCustomersSummary,
      isError: isErrorCustomersSummary,
      link_url: `/customers${last30DaysFilterQuery}`,
    },
    {
      key: "Successful Transactions",
      value: transactionsSummary?.last30_days_successful_tx_count || 0,
      isLoading: isLoadingTransactionsSummary,
      isSuccess: isSuccessTransactionsSummary,
      isError: isErrorTransactionsSummary,
      link_url: `/payments${last30DaysFilterQuery}&status=successful`,
    },
    {
      key: "Feedback",
      value: 0,
      isLoading: false,
      isSuccess: true,
      isError: false,
      link_url: `/feedback${last30DaysFilterQuery}`,
    },
    {
      key: "Payment Gateways",
      value: 3,
      isLoading: false,
      isSuccess: true,
      isError: false,
      link_url: `/payments${last30DaysFilterQuery}&sort=byGateway`,
    },
  ];

  const monthlyOrYearlyQueryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.monthlyOrYearlyStatusKey,
    options: [
      {
        key: "Monthly",
        value: tabValues.monthly,
      },
      {
        key: "Yearly",
        value: tabValues.yearly,
      },
    ],
  };

  const todayGrossSales = todaysTransactions
    ? todayGrossVolumeChartXYFormatter(todaysTransactions)
    : [];

  const totalSalesChartData = transactionsByPeriod
    ? totalSalesChartFormatter(
        transactionsByPeriod.filter(
          (transaction) => transaction.status === "successful"
        ),
        startChartPeriod,
        endChartPeriod
      )
    : [];

  const totalTransactionsChartData = transactionsByPeriod
    ? chartXYFormatter(transactionsByPeriod, startChartPeriod, endChartPeriod)
    : [];

  const transactionsByGatewayChartData: IMultiSeriesChartData[] =
    transactionsByGateway?.length
      ? transactionsByGateway.map((chartSeries) => ({
          label: chartSeries.gateway_code,
          data: chartXYFormatter(
            chartSeries.transaction_by_period,
            startChartPeriod,
            endChartPeriod
          ),
        }))
      : [
          {
            label: "",
            data: chartXYFormatter([], startChartPeriod, endChartPeriod),
          },
        ];

  console.log(
    "transactionsByGatewayChartData: ",
    transactionsByGatewayChartData
  );

  const totalSuccessfulTransactionsChartData = transactionsByPeriod
    ? chartXYFormatter(
        transactionsByPeriod.filter(
          (transaction) => transaction.status === "successful"
        ),
        startChartPeriod,
        endChartPeriod
      )
    : [];

  const totalFailedTransactionsChartData = transactionsByPeriod
    ? chartXYFormatter(
        transactionsByPeriod.filter(
          (transaction) => transaction.status === "failed"
        ),
        startChartPeriod,
        endChartPeriod
      )
    : [];

  // const totalCustomersChartData = transactionsByPeriod
  //   ? transactionsByCustomerByPeriodChartFormatter(
  //       transactionsByPeriod,
  //       startChartPeriod,
  //       endChartPeriod
  //     )
  //   : [];

  // console.log("totalCustomersChartData", totalCustomersChartData);

  const chartPeriodDisplayFormatter = (date: Date | string) => {
    return moment(date).format("MMM DD");
  };

  return (
    <View
      // primary_withdrawal_currency={payoutsSummary?.primary_withdrawal_currency}
      defaultCurrency={activeBalanceCurrency || ""}
      showOnboardingRequiredModal={showHomepageOnboardingModal}
      closeOnboardingRequiredModal={closeHomepageOnboardingModal}
      navigateToAccountSetup={navigateToAccountSetup}
      navigateToRoute={navigateToRoute}
      monthlyOrYearlyQueryStringTabs={monthlyOrYearlyQueryStringTabs}
      summaryTableData={summaryTableData}
      quickSummaryColumData={quickSummaryColumData}
      // timePeriod
      timeStampNow={moment(new Date().setHours(23, 59, 59, 50)).format(
        appConfig.timeFormat1
      )}
      chartType={chartType}
      setChartType={setChartType}
      startChartPeriod={startChartPeriod}
      endChartPeriod={endChartPeriod}
      setStartChartPeriodFromDatePicker={setStartChartPeriodFromDatePicker}
      setEndChartPeriodFromDatePicker={setEndChartPeriodFromDatePicker}
      chartPeriodDisplayFormatter={chartPeriodDisplayFormatter}
      // payouts
      isLoadingPayoutsSummary={isLoadingPayoutsSummary}
      isErrorPayoutsSummary={isErrorPayoutsSummary}
      payoutsSummary={payoutsSummary}
      // todays transactions
      isLoadingTodaysTransaction={isLoadingTodaysTransaction}
      isErrorTodaysTransaction={isErrorTodaysTransaction}
      todaysTransactions={isErrorTodaysTransaction ? [] : todaysTransactions}
      // transactions by period
      isLoadingTransactionsByPeriod={isLoadingTransactionsByPeriod}
      isErrorTransactionsByPeriod={isErrorTransactionsByPeriod}
      transactionsByPeriod={
        isErrorTransactionsByPeriod ? [] : transactionsByPeriod!
      }
      // transactions by gateway
      isLoadingTransactionsByGateway={isLoadingTransactionsByGateway}
      isErrorTransactionsByGateway={isErrorTransactionsByGateway}
      transactionsByGateway={
        isErrorTransactionsByGateway ? [] : transactionsByGateway!
      }
      gatewayChartTxStatus={gatewayChartTxStatus}
      setGatewayChartTxStatus={setGatewayChartTxStatus}
      // customers by period
      // isLoadingCustomersByPeriod={isLoadingCustomersByPeriod}
      // isSuccessCustomersByPeriod={isSuccessCustomersByPeriod}
      // customersByPeriod={isSuccessCustomersByPeriod ? customersByPeriod : []}
      // gross sales
      todayGrossSales={todayGrossSales}
      // display by period formatted data
      totalSalesChartData={totalSalesChartData}
      totalTransactionsChartData={totalTransactionsChartData}
      totalSuccessfulTransactionsChartData={
        totalSuccessfulTransactionsChartData
      }
      transactionsByGatewayChartData={transactionsByGatewayChartData}
      totalFailedTransactionsChartData={totalFailedTransactionsChartData}
      totalCustomersChartData={totalTransactionsChartData}
      // highRiskPaymentsChartData={highRiskPaymentsChartData}
      // disputeActivityChartData={disputeActivityChartData}
      // disputesChartData={disputesChartData}
    />
  );
};
